import { TITLE_COPY_LIMITS } from "../copy-policy";
/**
 * The desktop artwork's one output size: the band's artwork box is a FIXED
 * 1055x529 (`h-[529px] w-[1055px]`), never fluid, so 2x for a high-DPI screen is
 * exactly this and not a pixel more.
 */
export const PROMO_IMAGE_DESKTOP_TARGET = {
    width: 2110,
    height: 1058,
    aspectRatio: "1055:529",
};
/**
 * The mobile artwork's one output size: the box is `aspect-[900/740]` and runs
 * full-bleed up to the 900px break, so 2x covers the widest case — and a 430pt
 * phone at 3x is 1290 device px, well inside it.
 */
export const PROMO_IMAGE_MOBILE_TARGET = {
    width: 1800,
    height: 1480,
    aspectRatio: "45:37",
};
/**
 * The two screens' artwork slots, in the Editor's reading order (desktop first).
 *
 * SINGLE-TARGET EACH, and load-bearing twice over, for the reasons
 * {@link MEDIA_SCREEN_IMAGE_SLOTS} states:
 *
 *  - it is what makes the size floor PER SCREEN. `BlockMediaSlot` offers the
 *    library against `slot.targets[0]`, so a picture need only be large enough
 *    for the screen it is going into. A multi-target slot would apply
 *    `frame-multi-target-slot`'s all-or-nothing floor and forbid exactly the
 *    pairing this component exists for.
 *  - it keeps each screen's artwork its OWN stored reference. Deriving the
 *    second framing from the first would collapse the case where both screens
 *    name the same picture.
 *
 * THE TWO SIZES MUST DIFFER. A crop is identified by the size it was cropped to
 * (`cropId = "{w}x{h}"`), so a common size would make two pictures of one asset
 * indistinguishable from one served everywhere.
 *
 * NEITHER SLOT TAKES A CLIP. `accepts` is omitted, which reads as pictures only:
 * the markup is a `<picture>`/`<img>`, there is nothing here to play, and the
 * band carries a call to action a player's controls would compete with.
 *
 * The artwork is drawn under `object-contain` against the band's own tint, and a
 * crop is WebP with its alpha kept (the image engine never flattens), so a
 * transparent product render whose shape does not match its box is LETTERBOXED
 * rather than cut. The framing step chooses what is inside the crop.
 */
export const PROMO_IMAGE_SCREEN_SLOTS = [
    {
        key: "desktopImage",
        label: "Desktop picture",
        targets: [{ ...PROMO_IMAGE_DESKTOP_TARGET }],
    },
    {
        key: "mobileImage",
        label: "Mobile picture",
        targets: [{ ...PROMO_IMAGE_MOBILE_TARGET }],
    },
];
/**
 * No `layout`, deliberately: this band displays both pictures in every
 * rendering, so `filled-slot-never-silently-dropped`'s filled-but-hidden trap
 * cannot arise and there is nothing to warn an Editor about.
 *
 * The title is a TEXTAREA, not a text input, and that is the whole reason this
 * component's title differs from every sibling's. The heading renders
 * `white-space: pre-line` and the design's copy is two authored lines; a
 * single-line input cannot produce the break the treatment exists for.
 */
/**
 * The sizes this band's message may be set at, and the one it defaults to.
 *
 * Read twice from this one declaration: by the slot below, and by the renderer
 * in `@diality/ui`, so the menu cannot offer a step the renderer has no size for.
 *
 * L IS ALREADY WHAT THE BAND DRAWS, so adopting the control changes nothing
 * until an author chooses otherwise — unlike {@link FEATURED_PRESS_TITLE_SCALE},
 * whose default was deliberately a step above its shipped size. Note M is 60
 * here, not the 64 the other two blocks use: the steps are per-block and this
 * band's design says 60.
 */
export const PROMO_IMAGE_TITLE_SCALE = {
    default: "l",
    steps: ["xl", "l", "m"],
    sizes: "title",
};
export const promoImageSchema = {
    componentId: "PromoImage",
    scalarSlots: [
        { key: "title", ...TITLE_COPY_LIMITS, label: "Title", type: "textarea" },
        {
            key: "titleSize",
            label: "Title size",
            type: "type-scale",
            scale: PROMO_IMAGE_TITLE_SCALE,
        },
        { key: "ctaLabel", label: "CTA label", type: "text" },
        { key: "ctaHref", label: "CTA URL", type: "url" },
    ],
    mediaSlots: PROMO_IMAGE_SCREEN_SLOTS,
};
//# sourceMappingURL=promo-image.js.map